/**
 * APA formatting for the deck: in-text citations, quotes, reference entries and
 * the walk that collects every source a deck contains.
 *
 * The two outputs an author sees for a picture or a table are built elsewhere,
 * by two modules that share no formatter with each other:
 *
 *   * `apaFigureNote.js`      — the note under the figure on the slide.
 *   * `apaImageReference.js`  — the entry on the deck's reference slide.
 *
 * Both are re-exported here so the app's existing call sites keep one import.
 */

import {
  cleanValue,
  ensurePeriod,
  formatFullDate,
  formatInTextAuthor,
  formatPageSegment,
  formatRetrievalDate,
  formatReferenceAuthor,
  formatYearOnly,
  linkifySegments,
  normalizeContributors,
  normalizeDoi,
  segmentsToHtml,
  segmentsToRuns,
  segmentsToText
} from './apaShared';
import {
  buildFigureNote,
  buildMediaNote,
  buildTableNote,
  formatFigureNote,
  formatNoteAuthor,
  formatTableNote,
  noteLicense
} from './apaFigureNote';
import { buildImageReference, inferReferenceCategory } from './apaImageReference';

export {
  buildFigureNote,
  buildMediaNote,
  buildTableNote,
  formatFigureNote,
  formatNoteAuthor,
  formatTableNote,
  noteLicense,
  buildImageReference,
  inferReferenceCategory,
  linkifySegments,
  segmentsToHtml,
  segmentsToRuns,
  segmentsToText
};

const buildInTextCore = (meta = {}, options = {}) => {
  if (meta.sourceType === 'clipart_builtin') return '';
  const author = formatInTextAuthor(meta) || cleanValue(meta.title);
  const year = formatYearOnly(meta.date, meta.year);
  const parts = [];

  if (author) parts.push(author);
  if (year) parts.push(year);

  if (meta.sourceType !== 'video' && !options.omitPage) {
    const pageSegment = formatPageSegment(meta.page);
    if (pageSegment) parts.push(pageSegment);
  }

  return parts.join(', ');
};

export const formatInText = (meta = {}) => {
  const core = buildInTextCore(meta);
  return core ? `(${core})` : '';
};

export const formatMultiInText = (metas = []) => {
  const segments = (metas || [])
    .map((meta) => buildInTextCore(meta))
    .filter(Boolean);
  if (!segments.length) return '';
  return `(${segments.join('; ')})`;
};

export const formatInTextNarrative = (meta = {}) => {
  const author = formatInTextAuthor(meta) || cleanValue(meta.title);
  const year = formatYearOnly(meta.date, meta.year);
  if (!author && !year) return '';
  if (!author) return `(${year})`;
  return `${author} (${year})`;
};

export const formatQuote = (meta = {}) => {
  const text = cleanValue(meta.quoteText);
  if (!text) return '';
  const stripped = text.replace(/^"|"$/g, '');
  const quote = `"${stripped}"`;
  const citation = formatInText(meta);
  return citation ? `${quote} ${citation}.` : `${quote}.`;
};

/**
 * Everything an APA quote block displays: the quote body with its quotation
 * marks already applied, the in-text citation, and the DOI/URL link.
 *
 * One source of truth for the on-screen card, the HTML export and the
 * PowerPoint/PDF exports, so a lifted quote reads the same everywhere.
 * Empty fields come back as empty strings — the caller decides whether to
 * show a placeholder (editor) or drop the block (exports).
 */
export const buildApaQuoteContent = (block = {}) => {
  const sourceType = block?.sourceType === 'paraphrase' ? 'paraphrase' : 'quote';
  const sources = Array.isArray(block?.sources) && block.sources.length > 0
    ? block.sources
    : [{ author: block?.author || '', year: block?.year || '', page: block?.page || '' }];
  const metas = sources.map((source) => ({
    sourceType,
    author: source?.author,
    year: source?.year,
    page: source?.page,
    quoteText: block?.quoteText || ''
  }));

  // With no author, year or page anywhere, formatInText still returns the
  // "(n.d.)" fallback — a citation for nothing. A block nobody has filled in
  // yet should read as empty so the editor shows its prompt and the exports
  // leave the card out altogether.
  const hasSource = sources.some((source) => cleanValue(source?.author)
    || cleanValue(source?.year)
    || cleanValue(source?.page));
  const citation = metas.length > 1 ? formatMultiInText(metas) : formatInText(metas[0] || {});
  const body = cleanValue(block?.quoteText).replace(/^"|"$/g, '').trim();
  const doi = normalizeDoi(block?.doi);
  const url = cleanValue(block?.url);

  return {
    sourceType,
    isParaphrase: sourceType === 'paraphrase',
    // A paraphrase is the writer's own words, so it keeps no quotation marks.
    quoteText: body ? (sourceType === 'paraphrase' ? body : `“${body}”`) : '',
    citationText: hasSource && citation ? ensurePeriod(citation) : '',
    citationLink: doi ? `https://doi.org/${doi}` : url
  };
};

export const formatTableLabel = (meta = {}) => {
  if (meta.sourceType !== 'table') return null;
  const tableNumber = cleanValue(meta.section);
  const label = tableNumber ? `Table ${tableNumber}` : 'Table';
  const title = cleanValue(meta.title);
  if (!label && !title) return null;
  return { tableNumberLabel: label, tableTitle: title };
};

const formatReferenceCore = (meta = {}) => {
  const author = formatReferenceAuthor(meta);
  const aiOrganization = meta.sourceType === 'ai' && !author ? cleanValue(meta.publisher) : '';
  const title = cleanValue(meta.title);
  const container = cleanValue(meta.containerTitle || meta.source || meta.siteName || '');
  const url = cleanValue(meta.url);
  const doi = normalizeDoi(meta.doi);
  const volume = cleanValue(meta.volume);
  const issue = cleanValue(meta.issue);
  const pages = cleanValue(meta.pages);
  const publisher = cleanValue(meta.publisher);
  const version = cleanValue(meta.version);
  const prompt = cleanValue(meta.prompt);
  const notes = cleanValue(meta.notes);
  const fullDate = formatFullDate(meta.date, meta.year);
  const yearOnly = formatYearOnly(meta.date, meta.year);
  const retrievalPrefix = meta.retrievalDateEnabled ? formatRetrievalDate(meta.retrievalDate) : '';

  const hasReferenceCore = Boolean(author || title || container || yearOnly || fullDate || url || doi || publisher);
  if (!hasReferenceCore) return null;

  const leading = author
    ? ensurePeriod(author)
    : aiOrganization
      ? ensurePeriod(aiOrganization)
      : (title ? ensurePeriod(title) : '');
  const dateSegment = fullDate ? `(${fullDate}).` : (yearOnly ? `(${yearOnly}).` : '');
  const link = doi ? `https://doi.org/${doi}` : url;

  switch (meta.sourceType) {
    case 'video': {
      const titleSegment = title ? `${title} [Video].` : '';
      const siteSegment = container ? `${container}.` : '';
      const retrievalSegment = retrievalPrefix && link ? `${retrievalPrefix} ${link}` : '';
      return [leading, dateSegment, titleSegment, siteSegment, retrievalSegment || link].filter(Boolean).join(' ');
    }
    case 'journal': {
      const titleSegment = author && title ? `${title}.` : '';
      const journalSegment = container ? `${container}` : '';
      const volumeIssue = volume ? `${volume}${issue ? `(${issue})` : ''}` : '';
      const pagesSegment = pages ? `${pages}.` : '';
      const journalDetails = [journalSegment, volumeIssue].filter(Boolean).join(', ');
      const journalLine = [journalDetails, pagesSegment].filter(Boolean).join(', ');
      return [leading, dateSegment, titleSegment, journalLine, link].filter(Boolean).join(' ');
    }
    case 'book': {
      const titleSegment = author && title ? `${title}.` : '';
      const publisherSegment = container || publisher ? `${container || publisher}.` : '';
      return [leading, dateSegment, titleSegment, publisherSegment].filter(Boolean).join(' ');
    }
    case 'software': {
      const titleSegment = title ? `${title}${version ? ` (${version})` : ''} [Computer software].` : '';
      const publisherSegment = publisher ? `${publisher}.` : '';
      const retrievalSegment = retrievalPrefix && link ? `${retrievalPrefix} ${link}` : '';
      return [leading, dateSegment, titleSegment, publisherSegment, retrievalSegment || link].filter(Boolean).join(' ');
    }
    case 'ai': {
      const toolSegment = container || meta.source ? `${container || meta.source}${version ? ` (${version})` : ''} [Large language model].` : '';
      const promptSegment = prompt || title ? `Prompt: ${prompt || title}.` : '';
      const notesSegment = notes ? `${notes}.` : '';
      const retrievalSegment = retrievalPrefix && link ? `${retrievalPrefix} ${link}` : '';
      return [leading, dateSegment, toolSegment, promptSegment, notesSegment, retrievalSegment || link].filter(Boolean).join(' ');
    }
    case 'webpage':
    case 'quote':
    case 'paraphrase': {
      const titleSegment = author && title ? `${title}.` : '';
      const siteSegment = container ? `${container}.` : '';
      return [leading, dateSegment, titleSegment, siteSegment, link].filter(Boolean).join(' ');
    }
    case 'clipart_builtin':
      return '';
    default: {
      const titleSegment = author && title ? `${title}.` : '';
      const siteSegment = container ? `${container}.` : '';
      return [leading, dateSegment, titleSegment, siteSegment, link].filter(Boolean).join(' ');
    }
  }
};

// Pictures, tables and external clipart are referenced by the house style's
// image templates, which choose their shape from the record rather than from
// one formula — see apaImageReference.js.
const IMAGE_REFERENCE_TYPES = new Set(['image', 'table', 'clipart_external']);

/**
 * A reference entry with its italics kept: `{ segments, text }`.
 *
 * `segments` is what a renderer that can draw an italic title should use;
 * `text` is the same entry flattened, which is what every plain-string caller
 * has always received.
 */
export const buildReferenceContent = (meta = {}) => {
  const stored = cleanValue(meta.apaReference);
  if (stored) return { segments: [{ text: stored }], text: stored };

  if (IMAGE_REFERENCE_TYPES.has(meta.sourceType)) {
    const built = buildImageReference(meta);
    if (!built) return null;
    // An image with no artist of its own is not referenced as an image: the
    // film, webpage, book or article it came from is referenced instead.
    if (built.delegateSourceType) {
      const delegated = formatReferenceCore({ ...meta, sourceType: built.delegateSourceType });
      return delegated ? { segments: [{ text: delegated }], text: delegated } : null;
    }
    return { segments: built.segments, text: built.text };
  }

  const value = formatReferenceCore(meta);
  return value ? { segments: [{ text: value }], text: value } : null;
};

export const formatReference = (meta = {}) => {
  const content = buildReferenceContent(meta);
  return content ? content.text : null;
};

export const mapCitationToSourceMeta = (citation = {}) => {
  const rawType = cleanValue(citation.sourceType || citation.type);
  const sourceType = rawType === 'website'
    ? 'webpage'
    : (rawType || 'webpage');
  const author = cleanValue(citation.author)
    || [citation.authorLastName, citation.authorFirstName].map(cleanValue).filter(Boolean).join(', ');

  return {
    sourceType,
    author,
    authorFirstName: cleanValue(citation.authorFirstName),
    authorLastName: cleanValue(citation.authorLastName),
    contributors: Array.isArray(citation.contributors) ? citation.contributors : [],
    year: cleanValue(citation.year),
    date: cleanValue(citation.date),
    title: cleanValue(citation.title),
    source: cleanValue(citation.source),
    containerTitle: cleanValue(citation.containerTitle),
    publisher: cleanValue(citation.publisher),
    siteName: cleanValue(citation.siteName),
    version: cleanValue(citation.version),
    prompt: cleanValue(citation.prompt),
    notes: cleanValue(citation.notes),
    url: cleanValue(citation.url),
    doi: normalizeDoi(citation.doi),
    retrievalDateEnabled: Boolean(citation.retrievalDateEnabled),
    retrievalDate: cleanValue(citation.retrievalDate),
    volume: cleanValue(citation.volume),
    issue: cleanValue(citation.issue),
    pages: cleanValue(citation.pages),
    page: cleanValue(citation.page || citation.pages),
    includePage: citation.includePage !== false,
    license: cleanValue(citation.license),
    includeLicense: citation.includeLicense !== false,
    copyright: cleanValue(citation.copyright),
    isAdapted: Boolean(citation.isAdapted),
    // The house style picks a reference template from the kind of source, and
    // names the medium in the entry — neither shows up in the slide note.
    referenceCategory: cleanValue(citation.referenceCategory),
    medium: cleanValue(citation.medium),
    referenceTitle: cleanValue(citation.referenceTitle),
    articleTitle: cleanValue(citation.articleTitle),
    description: cleanValue(citation.description),
    caption: cleanValue(citation.caption),
    alt: cleanValue(citation.alt),
    citationId: cleanValue(citation.citationId || citation.id),
    citationMode: cleanValue(citation.citationMode),
    apaReference: cleanValue(citation.computed?.apaReference)
  };
};

/**
 * The note a citation block shows beneath its media.
 *
 * A citation torn off an image or a video has to keep reading exactly the way
 * it did while it was still part of the block, so a tear-off is a move, not an
 * edit. That splits along how the note is built:
 *
 *   * A figure or table note carries no URL. The note under an image never
 *     showed one — the full entry, link and all, is the reference list's job.
 *   * An in-text citation (a video) does show its link, because the video block
 *     shows the watch URL beside its own note.
 *
 * Returns null for a source with nothing to say, and for built-in clipart,
 * which needs no attribution at all.
 */
export const buildSourceNote = (citation = {}) => {
  const meta = mapCitationToSourceMeta(citation);
  if (meta.sourceType === 'clipart_builtin') return null;

  const isFigure = meta.sourceType === 'image' || meta.sourceType === 'clipart_external';
  const isTable = meta.sourceType === 'table';

  // A figure or table note carries italics and, for a Creative Commons licence,
  // a link; an in-text citation is one plain run.
  const note = isFigure ? buildFigureNote(meta) : (isTable ? buildTableNote(meta) : null);
  const text = note ? note.text : formatInText(meta);
  if (!text) return null;
  const segments = note ? note.segments : [{ text }];
  const plainText = note ? note.plainText : text;

  // A figure or table note never shows its address, and a citation may ask for
  // the same treatment with `showUrl: false` — a playlist card's note is the
  // note alone, with the address left to the deck's reference list. Everything
  // else (a video's watch link, a webpage, a quote) keeps showing it.
  const hidesUrl = citation.showUrl === false;
  const link = (isFigure || isTable || hidesUrl)
    ? ''
    : (meta.doi ? `https://doi.org/${meta.doi}` : cleanValue(meta.url));

  return { text, plainText, segments, link, meta };
};

export const buildImageSourceMeta = (item = {}, overrides = {}) => ({
  sourceType: item.imageSourceType || overrides.sourceType || 'image',
  author: item.imageAuthor || '',
  year: '',
  date: item.imageDate || '',
  title: item.imageTitle || '',
  source: item.imageSource || '',
  url: item.imageUrl || item.url || '',
  // Page and license are optional and can be explicitly excluded per item
  // (e.g. when the imported source has no page or license info).
  page: item.imageIncludePage === false ? '' : (item.imagePage || ''),
  includePage: item.imageIncludePage !== false,
  license: item.imageIncludeLicense === false ? '' : (item.imageLicense || ''),
  includeLicense: item.imageIncludeLicense !== false,
  isAdapted: Boolean(item.imageIsAdapted),
  // Reference-entry only: which reference template the source falls under, the
  // medium descriptor, and the sentence-case title if the author supplied one.
  referenceCategory: item.imageReferenceCategory || '',
  medium: item.imageMedium || '',
  referenceTitle: item.imageReferenceTitle || '',
  articleTitle: item.imageArticleTitle || '',
  caption: item.caption || '',
  alt: item.alt || '',
  ...overrides
});

export const buildVideoSourceMeta = (block = {}) => ({
  sourceType: 'video',
  author: block.videoAuthor || '',
  year: '',
  date: block.videoDate || '',
  title: block.videoTitle || '',
  source: block.videoSource || '',
  url: block.videoUrl || block.src || ''
});

/**
 * Every citation the deck's own blocks contribute, each tagged with where it
 * came from.
 *
 * The reference list is derived, not stored, so an author reviewing it has no
 * way to tell which slide put an entry there. `origin` carries that back:
 * enough to name the section and the kind of block, and the block id to find it
 * again. collectSourceMetasFromSections is the same walk with the tags dropped,
 * so the two can never disagree about what the deck contains.
 */
export const collectSourceEntriesFromSections = (sections = []) => {
  const results = [];

  (sections || []).forEach((section) => {
    const origin = (block, extra = {}) => ({
      sectionId: section?.id || '',
      sectionTitle: cleanValue(section?.title),
      blockId: block?.id || '',
      blockType: block?.type || '',
      ...extra
    });
    const push = (meta, block, extra) => results.push({
      meta,
      origin: origin(block, { included: true, ...extra })
    });

    (section?.blocks || []).forEach((block) => {
      if (block?.type === 'apa-citation' && Array.isArray(block.citations)) {
        block.citations.forEach((citation) => {
          // A torn-off citation still belongs to the block it came from.
          push(mapCitationToSourceMeta(citation), block, {
            detachedFrom: block.sourceBlockId || '',
            kind: block.variant === 'figure-note' ? 'detached' : 'citation'
          });
        });
      }
      if (block?.type === 'apa-quote') {
        const sourceType = block.sourceType === 'paraphrase' ? 'paraphrase' : 'quote';
        if (Array.isArray(block.sources) && block.sources.length > 0) {
          block.sources.forEach((source) => {
            push({
              sourceType,
              author: source.author || '',
              year: source.year || '',
              page: source.page || '',
              title: block.title || '',
              source: block.source || '',
              url: block.url || '',
              doi: block.doi || ''
            }, block);
          });
        } else {
          push({
            sourceType,
            author: block.author || '',
            year: block.year || '',
            page: block.page || '',
            title: block.title || '',
            source: block.source || '',
            url: block.url || '',
            doi: block.doi || ''
          }, block);
        }
      }
      // Sources whose citation is switched off are still collected, tagged
      // `included: false`, so a review panel can show what the deck holds and
      // offer it back. collectSourceMetasFromSections drops them, which is what
      // every other caller sees.
      //
      // Two switches reach this, and either one closes the door: the citation
      // itself, and whether it joins the deck's reference list. A source can be
      // noted under its picture without being listed at the back — a figure
      // reproduced from the author's own earlier slide, say — and both default
      // to on, so a deck saved before the second existed is unchanged.
      if (block?.type === 'image') {
        push(buildImageSourceMeta(block), block, {
          included: block.imageIncludeCitation !== false && block.imageAddToReferenceList !== false,
          includeField: 'imageIncludeCitation'
        });
      }
      if (block?.type === 'gallery') {
        (block.items || []).forEach((item, itemIndex) => {
          push(buildImageSourceMeta(item), block, {
            itemIndex,
            included: item.imageIncludeCitation !== false && item.imageAddToReferenceList !== false,
            includeField: 'imageIncludeCitation'
          });
        });
      }
      // A playlist card cites like a video and keeps its source under the same
      // field names, so the reference list treats the two the same way.
      if (block?.type === 'video' || block?.type === 'youtube-playlist') {
        // Off once the citation has been torn off into its own apa-citation
        // block, which the branch above already collects — without this the
        // reference list would carry the video twice.
        push(buildVideoSourceMeta(block), block, {
          included: block.videoIncludeCitation !== false && block.videoAddToReferenceList !== false,
          includeField: 'videoIncludeCitation'
        });
      }
      // A hand-written layout cites like any other block: the author fills the
      // sources in the block's editor, copies the in-text note into the layout
      // themselves, and the full reference is collected here for the deck's
      // reference list.
      if (block?.type === 'custom-html' && Array.isArray(block.citations)) {
        // A layout's citation is optional, and listing it is a second choice
        // again — same two switches the picture and video cards carry.
        const included = block.includeCitation !== false && block.addToReferenceList !== false;
        block.citations.forEach((citation) => {
          push(mapCitationToSourceMeta(citation), block, { included, includeField: 'includeCitation' });
        });
      }
      if (block?.type === 'html' && block.citation) {
        if (typeof block.citation === 'string') {
          push(mapCitationToSourceMeta({ sourceType: 'webpage', title: block.citation, computed: { apaReference: block.citation } }), block);
        } else {
          push(mapCitationToSourceMeta(block.citation), block);
        }
      }
      if (block?.type === 'multi-column-cards') {
        (block.columns || []).forEach((column, columnIndex) => {
          (column.cards || []).forEach((card, cardIndex) => {
            if (card?.image) {
              push(buildImageSourceMeta(card.image), block, {
                columnIndex,
                cardIndex,
                included: card.image.imageIncludeCitation !== false && card.image.imageAddToReferenceList !== false,
                includeField: 'imageIncludeCitation'
              });
            }
            if (card?.video) {
              push(buildVideoSourceMeta(card.video), block, {
                columnIndex,
                cardIndex,
                included: card.video.videoIncludeCitation !== false && card.video.videoAddToReferenceList !== false,
                includeField: 'videoIncludeCitation'
              });
            }
          });
        });
      }
    });
  });

  return results.filter((entry) => entry.meta && entry.meta.sourceType !== 'clipart_builtin');
};

export const collectSourceMetasFromSections = (sections = []) =>
  collectSourceEntriesFromSections(sections)
    .filter((entry) => entry.origin.included !== false)
    .map((entry) => entry.meta);

export const sortSourceMetas = (metas = []) => {
  return [...(metas || [])].sort((a, b) => {
    const aKey = cleanValue(formatReferenceAuthor(a) || a?.title || '').toLowerCase();
    const bKey = cleanValue(formatReferenceAuthor(b) || b?.title || '').toLowerCase();
    return aKey.localeCompare(bKey);
  });
};

// Reference strings are compared with punctuation and spacing normalized so
// that "Blair, P. (n.d.). Cartoon animation." and the same reference without a
// trailing period are recognized as one source.
const normalizeReferenceKey = (reference) => cleanValue(reference)
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .replace(/[.,;:]+$/, '')
  .trim();

// The surname (or organization name) of the first contributor, used to spot a
// bare author-only reference that a fuller reference already covers.
const primaryContributorKey = (meta = {}) => {
  const contributors = normalizeContributors(meta);
  if (!contributors.length) return '';
  const first = contributors[0];
  const name = first.isOrganization
    ? first.name
    : (first.lastName || first.firstName);
  return cleanValue(name).toLowerCase().replace(/[^a-z0-9]/g, '');
};

const linkKey = (meta = {}) => {
  const doi = normalizeDoi(meta.doi);
  if (doi) return `doi:${doi.toLowerCase()}`;
  const url = cleanValue(meta.url);
  return url ? `url:${url.toLowerCase().replace(/\/+$/, '')}` : '';
};

// A citation the author typed carries a citationId; auto-collected metas
// (image, video and HTML block sources) do not. Only the auto-collected ones
// are eligible for the "covered by a fuller reference" pass, so an author who
// deliberately enters two similar references always keeps both.
const isAutoCollected = (meta = {}) => !cleanValue(meta.citationId);

/**
 * Collapse the deck's sources down to one entry per work.
 *
 * Three passes, weakest claim last:
 *  1. Identical citationId — the same stored citation reached the list twice.
 *  2. Identical reference text or DOI among auto-collected metas.
 *  3. Auto-collected metas with no title that a fuller reference for the same
 *     author/year (or the same link) already covers — the "Blair. (n.d.)."
 *     stub that sits next to "Blair, P. (n.d.). Cartoon animation."
 */
export const dedupeSourcesByReference = (metas = []) => {
  const seenIds = new Set();
  const seenReferences = new Set();
  const seenLinks = new Set();
  const kept = [];

  (metas || []).forEach((meta) => {
    const citationId = cleanValue(meta.citationId);
    if (citationId) {
      if (seenIds.has(citationId)) return;
      seenIds.add(citationId);
      kept.push(meta);
      return;
    }
    const reference = formatReference(meta) || meta.apaReference;
    if (!reference) return;
    const key = normalizeReferenceKey(reference);
    if (seenReferences.has(key)) return;
    const link = linkKey(meta);
    if (link && seenLinks.has(link) && !cleanValue(meta.title)) return;
    seenReferences.add(key);
    if (link) seenLinks.add(link);
    kept.push(meta);
  });

  // Index the surviving entries so the subsumption pass can ask "does a fuller
  // reference for this author already exist?" without an O(n²) rescan.
  const titledByAuthor = new Set();
  const titledByLink = new Set();
  const referenceTexts = [];
  kept.forEach((meta) => {
    const reference = normalizeReferenceKey(formatReference(meta) || meta.apaReference || '');
    referenceTexts.push(reference);
    if (!cleanValue(meta.title)) return;
    const author = primaryContributorKey(meta);
    if (author) titledByAuthor.add(`${author}|${formatYearOnly(meta.date, meta.year)}`);
    const link = linkKey(meta);
    if (link) titledByLink.add(link);
  });

  return kept.filter((meta, index) => {
    if (!isAutoCollected(meta)) return true;
    if (cleanValue(meta.title)) return true;

    const author = primaryContributorKey(meta);
    if (author && titledByAuthor.has(`${author}|${formatYearOnly(meta.date, meta.year)}`)) return false;

    const link = linkKey(meta);
    if (link && titledByLink.has(link)) return false;

    // A stub whose whole reference is the opening of another entry adds
    // nothing — "Smith, J. (2020)." beside "Smith, J. (2020). Title. Pub."
    const reference = referenceTexts[index];
    if (!reference) return true;
    return !referenceTexts.some((other, otherIndex) => (
      otherIndex !== index
      && other.length > reference.length
      && other.startsWith(reference)
    ));
  });
};
